import { columnsFromHeaders, normalizeConfig } from './config_schema';

const CARD_BG = {
  gray: '#e6e6e6',
  white: '#ffffff',
  accent: '#eef2ff'
};

/**
 * @param {object} opts
 * @returns {import('./config_schema.js').PortalConfig}
 */
function baseConfig(opts) {
  const {
    templateKey,
    sheetName,
    headers,
    lookupHeader,
    lookupReference,
    urlParam,
    pageTitle,
    htmlFileName,
    webAppUrlPlaceholder,
    features,
    search,
    intro,
    rowCards,
    loansList,
    renewForm,
    minimalHtml,
    notFoundMessage,
    branding
  } = opts;

  const colReference = lookupReference || 'header';
  const columns = columnsFromHeaders(headers, colReference);
  const lookupCol = columns.find((c) => c.header === lookupHeader);

  return normalizeConfig({
    version: 1,
    templateKey,
    sheet: {
      name: sheetName,
      columns,
      defaultReference: lookupReference || 'header',
      lookup: {
        reference: lookupReference || 'header',
        header: lookupHeader,
        letter: lookupCol?.letter || 'A',
        number: lookupCol?.number || 1
      },
      urlParam
    },
    script: {
      htmlFileName: htmlFileName || 'Page',
      pageTitle,
      webAppUrlPlaceholder: webAppUrlPlaceholder || 'PASTE_DEPLOYMENT_URL_HERE',
      renewalDays: 14,
      allowAllFrames: true
    },
    features: features || { multiRowLookup: false, doPostRenew: false },
    html: {
      sections: {
        search: { enabled: true, ...search },
        intro: { enabled: Boolean(intro?.body || intro?.title), ...intro },
        rowCards: rowCards || [],
        loansList: loansList || { enabled: false },
        renewForm: renewForm || { enabled: false, renewalDays: 14, statusValues: ['Checked Out'] },
        notFound: { message: notFoundMessage || 'Not found. Check your ID and try again.' },
        minimal: minimalHtml ? { enabled: true } : { enabled: false },
        branding: branding || undefined
      }
    }
  });
}

export const DEFAULT_TEMPLATE_KEY = 'basic_starter';

function field(columnKey, label, fullWidth = false) {
  return { label, columnKey, fullWidth };
}

function card(id, title, bg, fields) {
  return { id, title, bg: CARD_BG[bg] || bg, fields };
}

const TEMPLATES = {
  basic_starter: baseConfig({
    templateKey: 'basic_starter',
    sheetName: 'Sheet1',
    headers: ['ID', 'Last Name', 'Client Name', 'Status'],
    lookupHeader: 'ID',
    lookupReference: 'letter',
    urlParam: 'id',
    pageTitle: 'Client Portal',
    htmlFileName: 'html',
    webAppUrlPlaceholder: 'Enter Web App Url Here',
    search: { enabled: false, label: 'ID', placeholder: '', buttonText: 'Look up' },
    intro: { enabled: false, title: '', subtitle: '', body: '' },
    rowCards: [],
    minimalHtml: false,
    notFoundMessage: 'ID not found'
  }),

  shipment_tracking: (() => {
    const cfg = baseConfig({
      templateKey: 'shipment_tracking',
      sheetName: 'Sheet1',
      headers: [
        'Confirmation_No',
        'Date',
        'Invoice_No',
        'PO_No',
        'Client',
        'Carrier',
        'Status',
        'Tracking_No',
        'Tracking_Link',
        'Shipped_Items',
        'Back_Order'
      ],
      lookupHeader: 'Confirmation_No',
      lookupReference: 'letter',
      urlParam: 'trackingNumber',
      pageTitle: 'Shipment Tracking',
      htmlFileName: 'TrackingPage',
      webAppUrlPlaceholder: 'Enter Web App Url Here',
      search: {
        label: 'Search confirmation no',
        placeholder: 'e.g. INV123-PO',
        buttonText: 'Search'
      },
      intro: { enabled: false, title: '', subtitle: '', body: '' },
      rowCards: [],
      notFoundMessage: 'Tracking number not found'
    });
    cfg.features = { multiRowLookup: false, doPostRenew: false, shipmentTracker: true };
    cfg.html.sections.branding = {
      ...cfg.html.sections.branding,
      enabled: true,
      companyName: 'Your Company',
      description:
        'Thank you for your order. Your order has been processed. Need help? Contact your customer service team.'
    };
    cfg.html.sections.shippingTracker = {
      enabled: true,
      queueStatus: 'IN SHIP QUEUE',
      shippedStatusLabel: 'SHIPPED',
      pageHeading: 'Order shipment tracking',
      pageSubheading: '',
      introEn: '',
      searchHelpEn:
        'The confirmation number is in your order confirmation email. It is usually your invoice number plus the first characters of your PO number.',
      searchHelpShort: 'Format: InvoiceNo-PO prefix (example: INV12345-AB)',
      footerText: 'Your Company: replace this footer with your address and website.',
      orderNotFound: 'Order not found.',
      shipmentNotFound: 'Tracking number not found.'
    };
    return cfg;
  })(),

  inventory: baseConfig({
    templateKey: 'inventory',
    sheetName: 'Sheet1',
    headers: [
      'SKU',
      'Product_Name',
      'Category',
      'Quantity_On_Hand',
      'Reorder_Level',
      'Location',
      'Unit_Price',
      'Supplier',
      'Last_Updated',
      'Notes'
    ],
    lookupHeader: 'SKU',
    urlParam: 'sku',
    pageTitle: 'Inventory Lookup',
    search: { label: 'Product SKU', placeholder: 'WIDGET-001', buttonText: 'Look up item' },
    intro: { title: '', subtitle: '', body: 'Enter a SKU to view stock and product details.' },
    rowCards: [
      card('product', 'Product', 'gray', [
        field('productName', 'Product'),
        field('category', 'Category'),
        field('sku', 'SKU')
      ]),
      card('stock', 'Stock', 'gray', [
        field('quantityOnHand', 'On hand'),
        field('reorderLevel', 'Reorder level'),
        field('location', 'Location')
      ]),
      card('details', 'Details', 'white', [
        field('unitPrice', 'Unit price', true),
        field('supplier', 'Supplier', true),
        field('lastUpdated', 'Last updated', true),
        field('notes', 'Notes', true)
      ])
    ]
  }),

  event_registry: baseConfig({
    templateKey: 'event_registry',
    sheetName: 'Sheet1',
    headers: [
      'Registration_ID',
      'Attendee_Name',
      'Email',
      'Event_Name',
      'Session',
      'Ticket_Type',
      'Check_In_Status',
      'Table_Seat',
      'Dietary_Notes',
      'Registered_Date'
    ],
    lookupHeader: 'Registration_ID',
    urlParam: 'reg',
    pageTitle: 'Event Registration',
    search: { label: 'Registration code', placeholder: 'REG-2026-001', buttonText: 'View registration' },
    intro: { title: '', subtitle: '', body: 'Find your event details and ticket information.' },
    rowCards: [
      card('attendee', 'Attendee', 'gray', [
        field('attendeeName', 'Name'),
        field('email', 'Email'),
        field('registrationId', 'Registration ID')
      ]),
      card('event', 'Event', 'gray', [
        field('eventName', 'Event'),
        field('session', 'Session'),
        field('ticketType', 'Ticket type'),
        field('registeredDate', 'Registered')
      ]),
      card('dayof', 'Day of event', 'gray', [
        field('checkInStatus', 'Check-in'),
        field('tableSeat', 'Table / seat'),
        field('dietaryNotes', 'Dietary notes', true)
      ])
    ]
  }),

  project_management: baseConfig({
    templateKey: 'project_management',
    sheetName: 'Sheet1',
    headers: [
      'Project_ID',
      'Project_Name',
      'Client',
      'Status',
      'Phase',
      'Due_Date',
      'Owner',
      'Next_Milestone',
      'Blockers',
      'Last_Updated',
      'Notes'
    ],
    lookupHeader: 'Project_ID',
    urlParam: 'project',
    pageTitle: 'Project Status Portal',
    search: { label: 'Project ID', placeholder: 'PRJ-1042', buttonText: 'View project' },
    intro: { title: '', subtitle: '', body: 'Enter your project ID to see status and next steps.' },
    rowCards: [
      card('overview', 'Overview', 'gray', [
        field('projectName', 'Project'),
        field('client', 'Client'),
        field('projectId', 'Project ID'),
        field('status', 'Status')
      ]),
      card('timeline', 'Timeline', 'gray', [
        field('phase', 'Phase'),
        field('dueDate', 'Due date'),
        field('nextMilestone', 'Next milestone'),
        field('lastUpdated', 'Last updated')
      ]),
      card('team', 'Team & blockers', 'white', [
        field('owner', 'Owner', true),
        field('blockers', 'Blockers', true),
        field('notes', 'Notes', true)
      ])
    ]
  }),

  client_portal: baseConfig({
    templateKey: 'client_portal',
    sheetName: 'Sheet1',
    headers: [
      'Portal_Code',
      'Client_Name',
      'Company',
      'Project_Name',
      'Status',
      'Phase',
      'Last_Updated',
      'Next_Deliverable',
      'Due_Date',
      'Invoice_Status',
      'Deliverables',
      'Latest_Update',
      'Account_Manager',
      'Contact_Email'
    ],
    lookupHeader: 'Portal_Code',
    urlParam: 'code',
    pageTitle: 'Client Project Portal',
    search: { label: 'Portal code', placeholder: 'CLIENT-2048', buttonText: 'View my project' },
    intro: {
      title: '',
      subtitle: '',
      body: 'Enter the code from your welcome email to see your project status and latest updates.'
    },
    rowCards: [
      card('project', 'Your project', 'gray', [
        field('clientName', 'Client'),
        field('company', 'Company'),
        field('projectName', 'Project'),
        field('status', 'Status')
      ]),
      card('progress', 'Progress', 'gray', [
        field('phase', 'Phase'),
        field('lastUpdated', 'Last updated'),
        field('nextDeliverable', 'Next deliverable'),
        field('dueDate', 'Due date')
      ]),
      card('billing', 'Deliverables & billing', 'white', [
        field('deliverables', 'Deliverables', true),
        field('invoiceStatus', 'Invoice status'),
        field('latestUpdate', 'Latest update', true)
      ]),
      card('team', 'Your team', 'accent', [
        field('accountManager', 'Account manager'),
        field('contactEmail', 'Contact email')
      ])
    ]
  }),

  library: (() => {
    const cfg = baseConfig({
      templateKey: 'library',
      sheetName: 'Checkouts',
      headers: [
        'Checkout_ID',
        'Patron_ID',
        'Patron_Name',
        'Book_Title',
        'Author',
        'Status',
        'Due_Date',
        'Checked_Out_Date',
        'Renewal_Count',
        'Shelf_Location',
        'Notes'
      ],
      lookupHeader: 'Patron_ID',
      urlParam: 'patron',
      pageTitle: 'Library Checkout Portal',
      search: { label: 'Patron or card ID', placeholder: 'PATRON-42', buttonText: 'View my loans' },
      intro: {
        title: '',
        subtitle: '',
        body: 'See what you have checked out and when it is due. Renew to extend your due date.'
      },
      rowCards: [],
      loansList: { enabled: true },
      renewForm: { enabled: true, renewalDays: 14, statusValues: ['Checked Out', 'Overdue'] }
    });
    cfg.features = { multiRowLookup: true, doPostRenew: true };
    return cfg;
  })(),

  blank: null
};

TEMPLATES.blank = null;

export const TEMPLATE_OPTIONS = [
  { key: 'blank', label: 'Blank' },
  { key: 'basic_starter', label: 'Basic example' },
  { key: 'shipment_tracking', label: 'Shipping Tracker' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'event_registry', label: 'Event Registry' },
  { key: 'project_management', label: 'Project Management' },
  { key: 'client_portal', label: 'Client Portal' },
  { key: 'library', label: 'Library' }
];

/**
 * @param {string} key
 * @returns {import('./config_schema.js').PortalConfig|null}
 */
export function getTemplate(key) {
  if (key === 'blank') return null;
  const t = TEMPLATES[key];
  return t ? normalizeConfig(structuredClone(t)) : null;
}

export function listTemplates() {
  return TEMPLATE_OPTIONS;
}
